import React from 'react';
import { FaCookieBite, FaInfoCircle, FaCog, FaShieldAlt } from 'react-icons/fa';

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-orange-600 to-orange-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">Cookie Policy</h1>
            <p className="text-xl md:text-2xl text-orange-100 max-w-3xl mx-auto">
              Learn about how we use cookies to enhance your experience
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-8">
              <strong>Last updated:</strong> {new Date().toLocaleDateString()}
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <FaCookieBite className="h-6 w-6 mr-2 text-orange-600" />
                What Are Cookies?
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Cookies are small text files that are placed on your device when you visit our website. They help us provide you with a better experience by remembering your preferences and enabling certain functionality.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <FaInfoCircle className="h-6 w-6 mr-2 text-orange-600" />
                How We Use Cookies
              </h2>
              <p className="text-gray-700 mb-4">
                We use cookies for several purposes to improve your experience on our platform:
              </p>
              <ul className="list-disc pl-6 text-gray-700">
                <li>Remember your login status and preferences</li>
                <li>Analyze how you use our platform to improve functionality</li>
                <li>Provide personalized content and recommendations</li>
                <li>Ensure platform security and prevent fraud</li>
                <li>Enable social media integration and sharing</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <FaCog className="h-6 w-6 mr-2 text-orange-600" />
                Types of Cookies We Use
              </h2>
              
              <div className="space-y-6">
                <div className="border-l-4 border-orange-500 pl-4">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Essential Cookies</h3>
                  <p className="text-gray-700 mb-2">
                    These cookies are necessary for the platform to function properly. They cannot be disabled.
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 text-sm">
                    <li>Authentication and session management</li>
                    <li>Security and fraud prevention</li>
                    <li>Load balancing and performance</li>
                  </ul>
                </div>

                <div className="border-l-4 border-blue-500 pl-4">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Functional Cookies</h3>
                  <p className="text-gray-700 mb-2">
                    These cookies enhance functionality and personalization but are not essential.
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 text-sm">
                    <li>Language and region preferences</li>
                    <li>User interface customizations</li>
                    <li>Remembering form data</li>
                  </ul>
                </div>

                <div className="border-l-4 border-green-500 pl-4">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Analytics Cookies</h3>
                  <p className="text-gray-700 mb-2">
                    These cookies help us understand how users interact with our platform.
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 text-sm">
                    <li>Page views and user journeys</li>
                    <li>Feature usage and performance metrics</li>
                    <li>Error tracking and debugging</li>
                  </ul>
                </div>

                <div className="border-l-4 border-purple-500 pl-4">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Marketing Cookies</h3>
                  <p className="text-gray-700 mb-2">
                    These cookies are used to deliver relevant advertisements and track campaign effectiveness.
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 text-sm">
                    <li>Ad targeting and personalization</li>
                    <li>Social media integration</li>
                    <li>Campaign performance tracking</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <FaShieldAlt className="h-6 w-6 mr-2 text-orange-600" />
                Managing Your Cookie Preferences
              </h2>
              <p className="text-gray-700 mb-4">
                You have control over which cookies you accept. Here's how to manage them:
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Browser Settings</h3>
              <p className="text-gray-700 mb-4">
                Most browsers allow you to control cookies through their settings. You can:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-6">
                <li>Block all cookies</li>
                <li>Allow only first-party cookies</li>
                <li>Delete existing cookies</li>
                <li>Set up notifications for new cookies</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Platform Settings</h3>
              <p className="text-gray-700 mb-4">
                You can also manage your cookie preferences through our platform settings in your account dashboard.
              </p>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-orange-800">
                  <strong>Note:</strong> Disabling certain cookies may affect the functionality of our platform and your user experience.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-Party Cookies</h2>
              <p className="text-gray-700 mb-4">
                We may use third-party services that set their own cookies. These include:
              </p>
              <ul className="list-disc pl-6 text-gray-700">
                <li>Google Analytics for website analytics</li>
                <li>Social media platforms for sharing features</li>
                <li>Payment processors for transaction security</li>
                <li>Customer support tools for assistance</li>
              </ul>
              <p className="text-gray-700 mt-4">
                These third parties have their own privacy policies and cookie practices. We recommend reviewing their policies for more information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Cookie Retention</h2>
              <p className="text-gray-700 mb-4">
                Different cookies have different retention periods:
              </p>
              <ul className="list-disc pl-6 text-gray-700">
                <li><strong>Session cookies:</strong> Deleted when you close your browser</li>
                <li><strong>Persistent cookies:</strong> Remain for a set period (typically 30 days to 2 years)</li>
                <li><strong>Essential cookies:</strong> Retained for the duration of your session</li>
                <li><strong>Analytics cookies:</strong> Typically retained for 2 years</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Updates to This Policy</h2>
              <p className="text-gray-700">
                We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. 
                We will notify you of any material changes by posting the updated policy on our website.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about our use of cookies, please contact us:
              </p>
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-700"><strong>Email:</strong> privacy@jhonks.com</p>
                <p className="text-gray-700"><strong>Phone:</strong> +234 (0) 800 123 4567</p>
                <p className="text-gray-700"><strong>Address:</strong> 123 Innovation Street, Lagos, Nigeria</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;
